'use client';

import { useRouter } from 'next/navigation';
import { FC, useMemo } from 'react';
import { toast } from 'sonner';
import { submitLeadMagnetLead } from '@/app/actions/submit-lead-magnet-lead';
import LeadMagnetBreakdown from '@/components/LeadMagnet/Breakdown';
import LeadMagnetIntro from '@/components/LeadMagnet/Intro';
import LeadMagnetLeadForm from '@/components/LeadMagnet/LeadForm';
import LeadMagnetQuestionScreen from '@/components/LeadMagnet/Question';
import LeadMagnetResult from '@/components/LeadMagnet/Result';
import LeadMagnetShell from '@/components/LeadMagnet/Shell';
import { LeadFormData } from '@/constants/lead-magnet-form';
import { toastErrorStyles } from '@/constants/toast';
import useLeadMagnetState from '@/hooks/useLeadMagnetState';
import { useModal } from '@/hooks/useModal';
import useThemeColor from '@/hooks/useThemeColor';
import { getUtmData } from '@/hooks/useUtmTracking';
import { BACK_FROM_FIRST_QUESTION, LEAD_MAGNET_HOME_URL, LEAD_MAGNET_THEME_COLORS } from '@/lib/lead-magnet/constants';
import { selectExitVariant } from '@/lib/lead-magnet/exit';
import { getLeadForm, getResultTier, getSuccessModal } from '@/lib/lead-magnet/get-result-tier';
import { buildLeadMagnetIdentity } from '@/lib/lead-magnet/identity';
import { selectFailedQuestions, selectScore } from '@/lib/lead-magnet/scoring';
import { LeadMagnetModel } from '@/types/lead-magnet';
interface Props {
  data: LeadMagnetModel;
}
const LeadMagnet: FC<Props> = ({
  data
}) => {
  const router = useRouter();
  const {
    openModal,
    closeModal
  } = useModal();
  const identity = useMemo(() => buildLeadMagnetIdentity(data), [data]);
  const [state, dispatch] = useLeadMagnetState(data.questions, identity);
  const {
    questions,
    progressLabelFormat,
    questionsBackground
  } = data;
  const question = questions[state.currentIndex] ?? null;
  const score = selectScore(questions, state.answers);
  const tier = getResultTier(score, data.resultTiers);
  const leadForm = getLeadForm(tier, data.leadForms);
  useThemeColor(LEAD_MAGNET_THEME_COLORS[state.stage]);
  const leave = (url: string) => {
    closeModal();
    router.push(url);
  };
  const handleExit = (leaveUrl: string) => {
    const variant = selectExitVariant(state, questions.length);
    const config = variant ? data.exitModals[variant] : null;
    if (!config) {
      leave(leaveUrl);
      return;
    }
    openModal('LEAD_MAGNET_EXIT', {
      config,
      variant,
      onLeave: () => leave(leaveUrl),
      onContinue: () => {
        closeModal();
        if (variant === 'intro') dispatch({
          type: 'START'
        });
        if (variant === 'result') dispatch({
          type: 'RESET'
        });
      }
    });
  };
  const openSuccessModal = () => {
    const config_0 = getSuccessModal(tier, data.successModal);
    if (!config_0) return;
    openModal('LEAD_MAGNET_SUCCESS', {
      config: config_0,
      score,
      total: questions.length,
      onConfirm: () => {
        closeModal();
        if (config_0.redirectUrl) router.push(config_0.redirectUrl);
      }
    });
  };
  const handleLeadSubmit = async (formData: LeadFormData) => {
    if (!tier) return;
    dispatch({
      type: 'SUBMIT_START'
    });
    try {
      await submitLeadMagnetLead({
        slug: data.slug,
        lead: formData,
        score,
        total: questions.length,
        tierKey: tier.key,
        formKey: leadForm?.key ?? null,
        answers: state.answers,
        utm: getUtmData()
      });
    } catch {
      dispatch({
        type: 'SUBMIT_ERROR'
      });
      toast.error('Error submitting form', toastErrorStyles);
      return;
    }
    dispatch({
      type: 'SUBMIT_SUCCESS',
      postSubmitAction: tier.postSubmitAction
    });
    if (tier.postSubmitAction === 'successModal') openSuccessModal();
  };
  const isBackLeaving = state.currentIndex === 0 && BACK_FROM_FIRST_QUESTION === 'exit';
  const handleBack = () => isBackLeaving ? handleExit(data.exitUrl) : dispatch({
    type: 'BACK'
  });
  const renderStage = () => {
    if (state.stage === 'question' && question) return <LeadMagnetQuestionScreen key={question.slug} question={question} nextQuestion={questions[state.currentIndex + 1] ?? null} index={state.currentIndex} total={questions.length} score={score} progressLabelFormat={progressLabelFormat} backLabel={data.backLabel} selectedValue={state.answers[question.slug] ?? null} canGoBack={state.currentIndex > 0 || BACK_FROM_FIRST_QUESTION !== 'stay'} background={questionsBackground} onAnswer={value => dispatch({
      type: 'ANSWER',
      slug: question.slug,
      value
    })} onBack={handleBack} />;
    if (state.stage === 'lead' && tier) return <LeadMagnetResult tier={tier} score={score} total={questions.length}>
					{leadForm ? <LeadMagnetLeadForm form={leadForm} isSubmitting={state.submitStatus === 'submitting'} onSubmit={handleLeadSubmit} /> : null}
				</LeadMagnetResult>;
    if (state.stage === 'breakdown' && tier?.breakdown) return <LeadMagnetBreakdown breakdown={tier.breakdown} signals={selectFailedQuestions(questions, state.answers)} score={score} total={questions.length} progressLabelFormat={progressLabelFormat} socials={data.socials} />;
    if (state.stage === 'lead' || state.stage === 'breakdown') return null;
    return <LeadMagnetIntro intro={data.intro} questionsBackground={questionsBackground} onStart={() => dispatch({
      type: 'START'
    })} />;
  };
  return <LeadMagnetShell logo={data.logo} exitLabel={data.exitLabel} onLogoClick={() => handleExit(LEAD_MAGNET_HOME_URL)} onExit={() => handleExit(data.exitUrl)}>
			{renderStage()}
		</LeadMagnetShell>;
};
export default LeadMagnet;